import { formatCurrency } from '@eriko/shared-types';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/** Tax tool payloads carry money in minor units (cents/pence). */
export function formatMinor(minor: number | null | undefined, currency = 'USD'): string {
  if (minor === null || minor === undefined) return '—';
  return formatCurrency(minor / 100, 2, currency);
}

export function TaxReportCard({
  icon: Icon,
  title,
  badge,
  children,
}: {
  icon: LucideIcon;
  title: string;
  badge?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <Icon className="h-4.25 w-4.25 text-surface-400" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">{title}</span>
        </div>
        {badge ? (
          <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
            {badge}
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export function TaxStatStrip({ children }: { children: ReactNode }) {
  return (
    <div className="grid auto-cols-fr grid-flow-col divide-x divide-surface-100 border-b border-surface-100">
      {children}
    </div>
  );
}

export function TaxStatCell({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string | number;
  emphasis?: boolean;
}) {
  return (
    <div className="px-4.5 py-3 text-center">
      <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-surface-500">
        {label}
      </div>
      <div
        className={`mt-1 font-mono text-sm font-semibold tabular-nums ${
          emphasis ? 'text-pos' : 'text-surface-900'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

/** A labelled money row used in the line-item tables. */
export function TaxLineRow({
  label,
  value,
  sublabel,
  strong,
}: {
  label: string;
  value: string;
  sublabel?: string | null;
  strong?: boolean;
}) {
  return (
    <tr
      className={`border-t border-surface-100 transition-colors hover:bg-surface-50 ${
        strong ? 'bg-surface-50/60' : ''
      }`}
    >
      <td className="px-4.5 py-1.75 text-[13px] text-surface-700">
        {label}
        {sublabel ? (
          <span className="ml-1.5 font-mono text-[11px] text-surface-400">{sublabel}</span>
        ) : null}
      </td>
      <td
        className={`px-4.5 py-1.75 text-right font-mono text-[13px] tabular-nums ${
          strong ? 'font-semibold text-surface-900' : 'text-surface-600'
        }`}
      >
        {value}
      </td>
    </tr>
  );
}

export function TaxEmptyState({ message }: { message: string }) {
  return <div className="px-4.5 py-10 text-center text-[13px] text-surface-500">{message}</div>;
}
